import { useEffect, useRef, useState, type MouseEvent } from "react";
import {
  ArrowDownRight,
  ArrowUpRight,
  BookOpen,
  BookOpenText,
  CalendarDays,
  ChevronDown,
  Globe2,
  Images,
  MapPin,
  Phone,
  Send,
  Sparkles,
  HandHeart,
  Flower2,
  Sun,
  X,
} from "lucide-react";
import { SiteFooter, SiteHeader } from "./components/SiteChrome";
import { EditorialRoute, PublishingPreview } from "./EditorialPages";
import { GalleryPage } from "./GalleryPage";
import { galleryHrefForProgramme } from "./content/programme-galleries";
import { site } from "./content/site";
import { design } from "./content/design";
import { Lotus } from "./components/TraditionArt";

const pathIcons = { book: BookOpen, reading: BookOpenText, calendar: CalendarDays, images: Images };
const programmeIcons = [Flower2, BookOpen, Sun, HandHeart];

function HomePage() {
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [enquiryTopic, setEnquiryTopic] = useState<"general" | "upanayanam">("general");
  const enquiryTrigger = useRef<HTMLAnchorElement>(null);
  const enquiryCloseButton = useRef<HTMLButtonElement>(null);
  const enquiryDialog = useRef<HTMLDialogElement>(null);
  const enquiryFallback = useRef<HTMLAnchorElement>(null);

  const openEnquiry = (event: MouseEvent<HTMLAnchorElement>, topic: "general" | "upanayanam") => {
    event.preventDefault();
    enquiryTrigger.current = event.currentTarget;
    setEnquiryTopic(topic);
    setEnquiryOpen(true);
  };

  const closeEnquiry = () => {
    enquiryDialog.current?.close();
    setEnquiryOpen(false);
    enquiryTrigger.current?.focus();
  };

  useEffect(() => {
    if (!enquiryOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    enquiryDialog.current?.showModal();
    enquiryCloseButton.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [enquiryOpen]);

  return (
    <div className="site-shell">
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <SiteHeader currentPath="/" />

      <main id="main">
        <section className="hero" id="top" aria-labelledby="hero-title">
          <Lotus className="hero-lotus" />
          <div className="container hero-grid">
            <div className="hero-copy">
              <p className="overline"><span>Vedic learning</span> · spiritual practice · teaching · service</p>
              <h1 id="hero-title">
                Sri Devinatha<br />
                <em>Mission</em>
              </h1>
              <p className="hero-note">{design.heroNote}</p>
              <p className="hero-intro">
                A living tradition dedicated to the Vedic way of life, spiritual practice,
                teaching and service.
              </p>
              <div className="hero-actions">
                <a className="button primary" href="#initiatives">
                  Explore the Mission <ArrowDownRight size={18} aria-hidden="true" />
                </a>
                <a className="text-link" href="#gurudeva">Meet the tradition</a>
              </div>
            </div>
            <figure className="hero-emblem">
              <div className="hero-emblem-frame">
                <img
                  src="/brand/emblem.jpg"
                  width="1018"
                  height="1280"
                  alt="Sri Devinatha Mission emblem depicting the rising sun, sacred flag, cow, lion and lotuses"
                />
              </div>
              <figcaption>
                <blockquote className="hero-quote">
                  <p lang="sa-Latn">{design.quote}</p>
                  <footer>{design.quoteMeaning.map((line) => <span key={line}>{line}</span>)}</footer>
                </blockquote>
              </figcaption>
            </figure>
          </div>
          <div className="container vows" aria-label="Vision and mission">
            <article>
              <span>Our vision</span>
              <p>{site.vision}</p>
            </article>
            <article>
              <span>Our mission</span>
              <p>{site.mission}</p>
            </article>
          </div>
        </section>

        <nav className="mission-paths container" aria-label="Explore the Mission">
          {design.paths.map((path) => {
            const Icon = pathIcons[path.icon];
            return <a href={path.href} key={path.title}><Icon aria-hidden="true" strokeWidth={1.4} /><span><strong>{path.title}</strong><small>{path.description}</small></span><ArrowUpRight aria-hidden="true" /></a>;
          })}
        </nav>

        <section className="section initiatives" id="initiatives" aria-labelledby="initiatives-title">
          <div className="container">
            <div className="section-heading split">
              <div>
                <p className="eyebrow">Practice in the world</p>
                <h2 id="initiatives-title">Programmes & initiatives</h2>
              </div>
              <p>
                Learning, initiation and seva are expressions of one shared purpose: enabling
                dharma to be studied, practised and carried forward.
              </p>
            </div>
            <div className="initiative-list">
              {site.initiatives.map((initiative, index) => {
                const Icon = programmeIcons[index];
                return (
                <article className="initiative" id={initiative.anchor} key={initiative.anchor}>
                  <span className="initiative-icon"><Icon aria-hidden="true" strokeWidth={1.4} /></span>
                  <div className="initiative-title">
                    <h3>{initiative.title}</h3>
                    <p>{initiative.summary}</p>
                  </div>
                  <details>
                    <summary>
                      Read more <span aria-hidden="true">＋</span>
                    </summary>
                    <div className="initiative-detail">
                      <div className="initiative-detail-copy">
                        {initiative.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}
                        {"benefits" in initiative && (
                          <ul>
                            {initiative.benefits.map((benefit) => <li key={benefit}>{benefit}</li>)}
                          </ul>
                        )}
                        {"announcement" in initiative && (
                          <p className="initiative-announcement">{initiative.announcement}</p>
                        )}
                        {"details" in initiative && (
                          <ul>
                            {initiative.details.map((detail) => <li key={detail}>{detail}</li>)}
                          </ul>
                        )}
                      </div>
                      {galleryHrefForProgramme(initiative.anchor) && (
                        <a className="initiative-gallery-link" href={galleryHrefForProgramme(initiative.anchor)}>
                          <Images aria-hidden="true" /> View photo collection
                          <ArrowUpRight aria-hidden="true" />
                        </a>
                      )}
                      {initiative.anchor === "page-3" && (
                        <a
                          className="initiative-gallery-link"
                          href={site.enquiryUrl}
                          target="_blank"
                          rel="noreferrer"
                          onClick={(event) => openEnquiry(event, "upanayanam")}
                        >
                          <Send aria-hidden="true" /> Request Upanayana <ArrowUpRight aria-hidden="true" />
                        </a>
                      )}
                    </div>
                  </details>
                </article>
              );})}
            </div>
          </div>
        </section>

        <section className="section origin" id="origin" aria-labelledby="origin-title">
          <div className="container origin-grid">
            <div className="origin-heading">
              <p className="eyebrow">Our origin</p>
              <h2 id="origin-title">{site.origin.title}</h2>
            </div>
            <div className="origin-story">
              <p>{site.origin.body}</p>
              <blockquote lang="sa">{site.origin.verse}</blockquote>
            </div>
          </div>
        </section>

        <section className="section lineage" id="gurudeva" aria-labelledby="teacher-title">
          <div className="container lineage-grid">
            <figure className="archival-figure">
              <div className="image-frame">
                <img
                  src="/images/gurudeva-dampathy.jpg"
                  alt="Pujyaśrī Devinatha Gurudeva and Pujyaśrī Devi seated beneath a tree"
                  width="1200"
                  height="1200"
                  loading="lazy"
                />
              </div>
              <figcaption>Pujyaśrī Gurudeva Dampathy</figcaption>
            </figure>
            <div className="lineage-copy">
              <p className="eyebrow light">The living tradition</p>
              <h2 id="teacher-title">{site.teacher.title}</h2>
              <p className="teacher-intro">{site.teacher.introduction}</p>
              <div className="life-notes">
                {site.teacher.life.map((item) => (
                  <details key={item.label}>
                    <summary>
                      <span>{item.label}</span>
                      <ChevronDown size={19} aria-hidden="true" />
                    </summary>
                    <p>{item.text}</p>
                  </details>
                ))}
              </div>
              <p className="legacy-note">{site.teacher.legacy}</p>
            </div>
          </div>
        </section>

        <section className="section ashram" id="ashram" aria-labelledby="ashram-title">
          <span className="anchor-target" id="page-8" aria-hidden="true" />
          <div className="container ashram-grid">
            <div className="ashram-copy">
              <p className="eyebrow light">Kailasa Konai · established 1954</p>
              <h2 id="ashram-title">{site.temple.title}</h2>
              <p>{site.temple.body}</p>
              <div className="ashram-actions">
                <a className="text-link light" href={site.locationUrl} target="_blank" rel="noreferrer">
                  View location <ArrowUpRight size={17} aria-hidden="true" />
                </a>
                <a className="text-link light" href={galleryHrefForProgramme("page-8")}>
                  View gallery <ArrowUpRight size={17} aria-hidden="true" />
                </a>
              </div>
            </div>
            <figure className="temple-figure">
              <img
                src="/images/sivasakthi-ashram.jpg"
                alt="Flower-adorned sannidhis at Sivasakthi Ashram"
                width="1200"
                height="1600"
                loading="lazy"
              />
              <figcaption>
                <Sparkles size={15} aria-hidden="true" /> Sivasakthi Ashram
              </figcaption>
            </figure>
          </div>
        </section>

        <section className="section teachings" id="teachings" aria-labelledby="teachings-title">
          <div className="container teachings-grid">
            <figure className="teaching-visual">
              <img
                src="/images/agni-fire.webp"
                alt="Flames rising from an Agni-kuṇḍa"
                width="460"
                height="460"
                loading="lazy"
              />
            </figure>
            <article>
              <p className="eyebrow">From the tradition</p>
              <h2 id="teachings-title">{site.teaching.title}</h2>
              <p>{site.teaching.body}</p>
            </article>
          </div>
        </section>

        <PublishingPreview />

        <section className="section contact" id="contact" aria-labelledby="contact-title">
          <div className="container contact-grid">
            <div>
              <p className="eyebrow">Begin a conversation</p>
              <h2 id="contact-title">Enquiries & contact</h2>
              <p className="contact-intro">
                For information about the Mission, its programmes or visits to the ashram, send
                an enquiry or reach us by phone.
              </p>
              <a
                className="button primary"
                href={site.enquiryUrl}
                target="_blank"
                rel="noreferrer"
                onClick={(event) => openEnquiry(event, "general")}
              >
                Send an enquiry <Send size={17} aria-hidden="true" />
              </a>
            </div>
            <address className="contact-details">
              <div>
                <Phone aria-hidden="true" />
                <span>
                  {site.phones.map((phone) => (
                    <a key={phone} href={`tel:${phone.replace(/[^+\d]/g, "")}`}>{phone}</a>
                  ))}
                </span>
              </div>
              <div>
                <MapPin aria-hidden="true" />
                <span>
                  {site.location}
                  <a href={site.locationUrl} target="_blank" rel="noreferrer">Open in Maps</a>
                </span>
              </div>
              <div>
                <Globe2 aria-hidden="true" />
                <span>
                  <a href={site.website}>{site.website.replace("https://", "")}</a>
                </span>
              </div>
            </address>
          </div>
        </section>
      </main>

      <SiteFooter />

      {enquiryOpen && (
          <dialog ref={enquiryDialog} className="enquiry-dialog" aria-labelledby="enquiry-title" onKeyDown={(event) => {
            if (event.key !== "Tab") return;
            if (event.shiftKey && event.target === enquiryCloseButton.current) {
              event.preventDefault();
              enquiryFallback.current?.focus();
            } else if (!event.shiftKey && event.target === enquiryFallback.current) {
              event.preventDefault();
              enquiryCloseButton.current?.focus();
            }
          }} onCancel={(event) => {
            event.preventDefault();
            closeEnquiry();
          }}>
            <div className="dialog-heading">
              <div>
                <p className="eyebrow">Sri Devinatha Mission</p>
                <h2 id="enquiry-title">{enquiryTopic === "upanayanam" ? "Upanayanam for All enquiry" : "Send an enquiry"}</h2>
              </div>
              <button ref={enquiryCloseButton} type="button" aria-label="Close enquiry form" onClick={closeEnquiry}><X /></button>
            </div>
            {enquiryTopic === "upanayanam" && <p className="enquiry-topic-note">Please mention Upanayanam for All in your message.</p>}
            <iframe
              src={site.enquiryUrl}
              title="Enquiry form for Sri Devinatha Mission"
              referrerPolicy="strict-origin-when-cross-origin"
            />
            <p className="form-fallback">
              If the form does not appear, <a ref={enquiryFallback} href={site.enquiryUrl} target="_blank" rel="noreferrer">open it separately</a>.
            </p>
          </dialog>
      )}
    </div>
  );
}

function App({ pathname }: { pathname?: string }) {
  const currentPath = pathname ?? (typeof window === "undefined" ? "/" : window.location.pathname);
  if (currentPath.startsWith("/gallery")) return <GalleryPage />;
  if (currentPath.startsWith("/articles") || currentPath.startsWith("/events")) {
    return <EditorialRoute pathname={currentPath} />;
  }
  return <HomePage />;
}

export default App;
